import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Check } from "lucide-react";

export default function Premium() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/payments/plans"); setPlans(data.plans || []); }
      catch (e) { setErr(formatApiError(e)); }
    })();
  }, []);

  const checkout = async (lookup_key) => {
    if (!user) { window.location.href = "/login"; return; }
    setBusy(lookup_key); setErr("");
    try {
      const origin_url = window.location.origin;
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url });
      window.location.href = data.checkout_url;
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(""); }
  };

  return (
    <div className="min-h-screen">
      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" data-testid="premium-logo"><Logo /></Link>
          <Link to={user ? "/app" : "/login"} className="text-sm text-navy hover:underline">
            {user ? "Back to app" : "Sign in"}
          </Link>
        </div>
      </header>
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/70 border border-rose/20 text-xs font-medium text-rose">
            <Sparkles size={14} /> Wholesome Premium
          </div>
          <h1 className="font-display text-5xl md:text-6xl mt-4 text-navy leading-tight">
            More depth. Less noise. <em className="text-rose">Genuine priority.</em>
          </h1>
          <p className="text-navy/70 mt-3 max-w-xl mx-auto">
            Support the platform and unlock deeper compatibility insights, unlimited messaging and more.
          </p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {plans.map((p) => (
            <div key={p.lookup_key} className="glass rounded-3xl p-8 border border-white/60" data-testid={`plan-${p.lookup_key}`}>
              <div className="text-xs uppercase tracking-widest text-rose">{p.name}</div>
              <div className="font-display text-4xl text-navy mt-1">{p.price}</div>
              <ul className="mt-5 space-y-2 text-sm text-navy/80">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2"><Check size={14} className="text-sage mt-1" /> {f}</li>
                ))}
              </ul>
              <button data-testid={`checkout-${p.lookup_key}`} onClick={() => checkout(p.lookup_key)} disabled={busy === p.lookup_key}
                className="mt-6 btn-pill w-full bg-rose text-white hover:bg-rose-600 hover:scale-[1.01] disabled:opacity-60">
                {busy === p.lookup_key ? "Redirecting…" : "Continue to secure checkout"}
              </button>
            </div>
          ))}
        </div>
        {err && <div className="text-sm text-rose-600 mt-4" data-testid="premium-error">{err}</div>}
        <div className="mt-10 text-xs text-navy/50 text-center">
          Payments are securely processed by Stripe. Cancel any time. Use test card 4242 4242 4242 4242 in test mode.
        </div>
      </div>
    </div>
  );
}
